export default class HeroText extends HTMLElement {
  connectedCallback() {
    this.prepare();

    if (HeroText.prefersReducedMotion()) {
      HeroText.showText(this);
    } else {
      this.decodeText();
    }
  }

  /**
   * Splits the text into lines at its line breaks, and every letter into a span of its own. The
   * spans are hidden from assistive technologies, which read the whole text instead. The lines are
   * spans too, as the element is placed inside a heading, which holds no paragraphs.
   */
  prepare() {
    const lines = this.innerHTML
        .split(/<br\s*\/?>/i)
        .map((html) => HeroText.textOf(html))
        .filter((line) => line.length > 0),
      label = document.createElement('span'),
      element = (tagName, className, text) => {
        const el = document.createElement(tagName);
        el.className = className;
        if (text !== undefined) {
          el.textContent = text;
        }
        return el;
      };

    label.className = 'visually-hidden';
    label.textContent = lines.join(' ');

    const sections = lines.map((line) => {
      const section = element('span', 'decode-text');
      section.setAttribute('aria-hidden', 'true');

      line.split(' ').forEach((word, index) => {
        if (index > 0) {
          section.append(element('span', 'space', ' '));
        }

        const container = element('span', 'word-container');
        [...word].forEach((letter) => container.append(element('span', 'text-animation', letter)));
        section.append(container);
      });

      return section;
    });

    this.replaceChildren(label, ...sections);
  }

  /**
   * Text of an HTML fragment, with its white space, line feeds included, collapsed
   * @param {string} html HTML fragment
   * @returns {string}
   */
  static textOf(html) {
    const template = document.createElement('template');
    template.innerHTML = html;

    return template.content.textContent.replace(/\s+/g, ' ').trim();
  }

  /**
   * @returns {boolean} True if the visitor asked for less motion
   */
  static prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  /**
   * Shows every letter at once, without the animation
   * @param {HTMLElement} root Element holding the letters
   */
  static showText(root) {
    root.querySelectorAll('.decode-text .text-animation')
      .forEach((letter) => letter.classList.add('state-3'));
  }

  /**
   * Reveals the letters of this element in a random order
   */
  decodeText() {
    const $char = this.querySelectorAll('.decode-text .text-animation'),
      state = [];

    for (let i = 0, j = $char.length; i < j; i++) {
      $char[i].classList.remove('state-1', 'state-2', 'state-3');
      state[i] = i;
    }

    const shuffled = HeroText.shuffle(state);

    for (let i = 0, j = shuffled.length; i < j; i++) {
      const child = $char[shuffled[i]],

        state1Time = Math.round(Math.random() * (2000 - 500)) + 50;
      if (child.classList.contains('text-animation')) {
        setTimeout(() => HeroText.firstStages(child), state1Time);
      }
    }
  }

  static firstStages(child) {
    if (child.classList.contains('state-2')) {
      child.classList.add('state-3');
    } else if (child.classList.contains('state-1')) {
      child.classList.add('state-2');
    } else if (!child.classList.contains('state-1')) {
      child.classList.add('state-1');
      setTimeout(() => HeroText.secondStages(child), 70);
    }
  }

  static secondStages(child) {
    if (child.classList.contains('state-1')) {
      child.classList.add('state-2');
      setTimeout(() => HeroText.thirdStages(child), 70);
    } else if (!child.classList.contains('state-1')) {
      child.classList.add('state-1');
    }
  }

  static thirdStages(child) {
    if (child.classList.contains('state-2')) {
      child.classList.add('state-3');
    }
  }

  /**
   * Shuffle an array
   * @param {Array<int>} input
   * @returns {Array<int>}
   */
  static shuffle(input) {
    const array = input.slice(0);
    let currentIndex = array.length,
      temporaryValue,
      randomIndex;

    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;

      temporaryValue = array[currentIndex];
      array[currentIndex] = array[randomIndex];
      array[randomIndex] = temporaryValue;
    }
    return array;
  }
}

window.customElements.define('hero-text', HeroText);
